import React, { useLayoutEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigateBackToPortfolio } from '../../portfolioNavigation';

const Page = styled.div`
  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  padding: 5rem 2rem 5.5rem;

  @media (max-width: 640px) {
    padding: 3rem 1.25rem 4rem;
  }
`;

const Container = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const BackButton = styled(motion.button)`
  background: none;
  border: none;
  font-size: 16px;
  font-weight: 500;
  color: var(--text-muted);
  cursor: pointer;
  margin: 0 0 2rem 140px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: var(--text);
  }

  @media (max-width: 960px) {
    margin-left: 24px;
  }

  @media (max-width: 640px) {
    margin: 0 0 1.35rem;
  }
`;

const Hero = styled.section`
  margin-left: 140px;
  margin-right: 140px;
  margin-bottom: 2.75rem;

  @media (max-width: 960px) {
    margin-left: 24px;
    margin-right: 24px;
  }

  @media (max-width: 640px) {
    margin-left: 0;
    margin-right: 0;
  }
`;

const Title = styled(motion.h1)`
  margin: 0 0 0.8rem;
  font-family: var(--font-serif);
  font-size: 32px;
  font-weight: 500;
  color: var(--text);
  letter-spacing: -0.02em;
  line-height: 1.15;

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const Intro = styled(motion.p)`
  margin: 0 0 1.4rem;
  font-size: 18px;
  font-weight: 400;
  color: var(--text-muted);
  line-height: 1.55;
  letter-spacing: -0.01em;
  max-width: 780px;
`;

const MetaRow = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
`;

const metaPillStyles = `
  border-radius: 999px;
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 8px 16px;
  font-size: 16px;
  color: var(--text-muted);
`;

const MetaPill = styled.span`
  ${metaPillStyles}
`;

const MetaPillLink = styled.a`
  ${metaPillStyles}
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover {
    color: var(--text);
    border-color: var(--text);
  }
`;

/** Shared pill for Freebites “View live product” and in-page CTAs */
export const freebitesAccentPillCss = css`
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);

  &:hover {
    color: var(--text);
    background: var(--bg);
    border-color: var(--text);
  }

  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 2px;
  }
`;

const MetaPillLinkAccent = styled.a`
  ${metaPillStyles}
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  font-weight: 500;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  ${freebitesAccentPillCss}
`;

const HeroImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 12px;
  margin-top: 1.5rem;
`;

const HeroImageFollowText = styled(motion.p)`
  margin: 1rem 0 0;
  font-size: 18px;
  font-weight: 400;
  color: var(--text-muted);
  line-height: 1.55;
  letter-spacing: -0.01em;
  max-width: 780px;
`;

const IntroFollowingHero = styled(Intro)`
  margin-top: 1.25rem;
`;

const HeroAfterBlock = styled.div`
  margin-top: 1.25rem;
  max-width: 780px;
`;

const StoryGrid = styled.div`
  display: grid;
  /* Nav sits in the left inset (0–140px); story column starts where the hero image starts */
  grid-template-columns: 140px minmax(0, 1fr);
  column-gap: 0;
  margin-left: 0;
  margin-right: 140px;

  ${(p) =>
    p.$noNav
      ? `
    grid-template-columns: minmax(0, 1fr);
    margin-left: 140px;
  `
      : ''}

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    margin-left: 24px;
    margin-right: 24px;
  }

  @media (max-width: 640px) {
    margin-left: 0;
    margin-right: 0;
  }
`;

const SideNav = styled.nav`
  position: sticky;
  top: 32px;
  align-self: start;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding-right: 1rem;
  min-width: 0;

  @media (max-width: 960px) {
    display: none;
  }
`;

const SideLink = styled.a`
  font-size: 16px;
  font-weight: 400;
  color: var(--text-muted);
  text-decoration: none;
  padding: 0.2rem 0;
  transition: color 0.15s ease, font-weight 0.15s ease;

  &:hover {
    color: var(--text);
  }

  ${(p) =>
    p.$active
      ? `
    font-weight: 500;
    color: var(--text);
  `
      : ''}
`;

const Story = styled.div`
  max-width: 760px;
`;

const StorySection = styled.section`
  scroll-margin-top: 32px;
  margin-bottom: 3.1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.85rem;
  font-family: var(--font-serif);
  font-size: 24px;
  font-weight: 500;
  color: var(--text);
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const Paragraph = styled.p`
  margin: 0 0 1rem;
  font-size: 18px;
  color: var(--text-muted);
  line-height: 1.55;
  letter-spacing: -0.01em;
`;

const Placeholder = styled.div`
  margin-top: 1.2rem;
  border-radius: 12px;
  border: 1px dashed var(--border);
  background: var(--surface);
  padding: 1.25rem 1.1rem;
`;

const PlaceholderTitle = styled.p`
  margin: 0 0 0.35rem;
  font-size: 16px;
  font-weight: 500;
  color: var(--text);
`;

const PlaceholderNote = styled.p`
  margin: 0;
  font-size: 16px;
  color: var(--text-muted);
  line-height: 1.45;
`;

const sectionAnim = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const CaseStudySection = ({ id, title, children }) => (
  <StorySection id={id}>
    <SectionTitle>{title}</SectionTitle>
    {children}
  </StorySection>
);

export const StoryParagraph = Paragraph;

export const GraphicPlaceholder = ({ title, note }) => (
  <Placeholder>
    <PlaceholderTitle>{title}</PlaceholderTitle>
    <PlaceholderNote>{note}</PlaceholderNote>
  </Placeholder>
);

const Figure = styled.figure`
  margin: 1.35rem 0 0;
`;

const FigureImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
`;

const FigureVideo = styled.video`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
`;

const VideoFigure = styled.figure`
  margin-top: ${(p) => (p.$flushTop ? 0 : '1.35rem')};
  margin-bottom: 0;

  ${(p) =>
    p.$maxWidth
      ? p.$alignLeft
        ? `
    max-width: ${p.$maxWidth};
    margin-left: 0;
    margin-right: auto;
  `
        : `
    max-width: ${p.$maxWidth};
    margin-left: auto;
    margin-right: auto;
  `
      : `
    margin-left: 0;
    margin-right: 0;
  `}
`;

const Figcaption = styled.figcaption`
  margin: 0.7rem 0 1.25rem;
  font-size: 16px;
  color: var(--text-muted);
  line-height: 1.45;
`;

export const CaseStudyFigure = ({ src, alt, caption }) => (
  <Figure>
    <FigureImg src={src} alt={alt || ''} loading="lazy" />
    {caption ? <Figcaption>{caption}</Figcaption> : null}
  </Figure>
);

export const CaseStudyVideo = ({ src, caption, ariaLabel, maxWidth, align = 'center', flushTop = false }) => (
  <VideoFigure $maxWidth={maxWidth} $alignLeft={align === 'left'} $flushTop={flushTop}>
    <FigureVideo
      controls
      playsInline
      preload="metadata"
      src={src}
      aria-label={ariaLabel || undefined}
    />
    {caption ? <Figcaption>{caption}</Figcaption> : null}
  </VideoFigure>
);

const CaseStudyTemplate = ({
  title,
  intro,
  details,
  sectionNav = [],
  backTarget = 'selected-work',
  heroImageSrc,
  heroImageAlt,
  belowHeroImage,
  introAfterHero = false,
  afterIntro,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const serializedSectionIds =
    Array.isArray(sectionNav) && sectionNav.length > 0
      ? sectionNav.map((s) => s.id).join(',')
      : '';

  const sectionIds = useMemo(
    () => (serializedSectionIds ? serializedSectionIds.split(',') : []),
    [serializedSectionIds],
  );

  const [activeSectionId, setActiveSectionId] = useState(sectionIds[0] || '');

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /**
   * Sidebar “current section” uses only viewport geometry (no scrollY math).
   * Active = last section in nav order whose top edge is at or above this line (px from viewport top).
   * Matches StorySection scroll-margin-top so anchor jumps and scroll spy agree.
   */
  useLayoutEffect(() => {
    if (sectionIds.length === 0) return;

    const markerPx = 200;

    const updateActiveSection = () => {
      let current = sectionIds[0];
      for (let i = 0; i < sectionIds.length; i++) {
        const id = sectionIds[i];
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= markerPx) {
          current = id;
        }
      }
      setActiveSectionId(current);
    };

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();

    window.addEventListener('scroll', schedule, { passive: true });
    document.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('scroll', schedule);
      vv.addEventListener('resize', schedule);
    }

    /** Re-run when smooth-scroll / layout settles; some browsers batch scroll poorly with RAF alone. */
    const io = new IntersectionObserver(schedule, {
      root: null,
      rootMargin: `-${markerPx}px 0px -35% 0px`,
      threshold: [0, 0.01, 0.05, 0.25, 0.5, 1],
    });

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      document.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      if (vv) {
        vv.removeEventListener('scroll', schedule);
        vv.removeEventListener('resize', schedule);
      }
      io.disconnect();
    };
  }, [sectionIds]);

  const handleBack = () => {
    navigateBackToPortfolio(navigate, location.state, backTarget);
  };

  const hasSectionNav = Array.isArray(sectionNav) && sectionNav.length > 0;

  return (
    <Page>
      <Container>
        <BackButton onClick={handleBack} whileHover={{ x: -5 }} transition={{ duration: 0.2 }}>
          ← Back
        </BackButton>

        <Hero>
          <Title initial="hidden" animate="visible" variants={sectionAnim}>
            {title}
          </Title>
          {!introAfterHero ? (
            <Intro initial="hidden" animate="visible" variants={sectionAnim}>
              {intro}
            </Intro>
          ) : null}
          <MetaRow initial="hidden" animate="visible" variants={sectionAnim}>
            {details.map((detail) => {
              const key = detail.href
                ? `link-${detail.href}`
                : `${detail.label}-${detail.value}`;
              if (detail.href) {
                const LinkComponent = detail.accent ? MetaPillLinkAccent : MetaPillLink;
                return (
                  <LinkComponent
                    key={key}
                    href={detail.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {detail.label ? (
                      <>
                        <strong>{detail.label}: </strong>
                        {detail.value}
                      </>
                    ) : (
                      detail.value
                    )}
                  </LinkComponent>
                );
              }
              return (
                <MetaPill key={key}>
                  <strong>{detail.label}: </strong>
                  {detail.value}
                </MetaPill>
              );
            })}
          </MetaRow>
          {heroImageSrc ? (
            <HeroImage src={heroImageSrc} alt={heroImageAlt || ''} />
          ) : null}
          {heroImageSrc && belowHeroImage ? (
            <HeroImageFollowText initial="hidden" animate="visible" variants={sectionAnim}>
              {belowHeroImage}
            </HeroImageFollowText>
          ) : null}
          {introAfterHero ? (
            <>
              <IntroFollowingHero initial="hidden" animate="visible" variants={sectionAnim}>
                {intro}
              </IntroFollowingHero>
              {afterIntro ? <HeroAfterBlock>{afterIntro}</HeroAfterBlock> : null}
            </>
          ) : afterIntro ? (
            <HeroAfterBlock>{afterIntro}</HeroAfterBlock>
          ) : null}
        </Hero>

        {hasSectionNav || children ? (
          <StoryGrid $noNav={!hasSectionNav}>
            {hasSectionNav ? (
              <SideNav aria-label="Case study sections">
                {sectionNav.map((section) => (
                  <SideLink
                    key={section.id}
                    href={`#${section.id}`}
                    $active={activeSectionId === section.id}
                    aria-current={activeSectionId === section.id ? 'location' : undefined}
                  >
                    {section.label}
                  </SideLink>
                ))}
              </SideNav>
            ) : null}
            <Story>{children}</Story>
          </StoryGrid>
        ) : null}
      </Container>
    </Page>
  );
};

export default CaseStudyTemplate;
